import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { A as Autoplay, p as playAutoplay } from './autoplay-I8xLCyCo.js';
import { a as addAccessibility, A as Accessibility } from './accessibility-_zCLcLLO.js';
import { a as addDots } from './dots-By_KY3Cn.js';

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const DESKTOP_SLIDES_PER_VIEW = 2;

/**
 * Hero banner Web Component that renders a responsive Embla carousel with
 * autoplay, dot navigation, and axis switching between horizontal
 * (mobile) and vertical (desktop ≥ 1024 px).
 *
 * @element hero-banner-cards
 */
class HeroBannerCards extends HTMLElement {
  /** @type {HTMLElement | null} Embla scroll container used as the viewport root. */
  #emblaViewport = this.querySelector('.embla__viewport');

  /** @type {HTMLElement | null} Direct parent of all slide elements inside the viewport. */
  #emblaContainer = this.querySelector('.embla__container');

  /** @type {import('embla-carousel').EmblaCarouselType | undefined} Embla carousel API instance. */
  #emblaApi;

  /** @type {MediaQueryList} Desktop breakpoint used for equal-height carousel behavior. */
  #desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);

  /** @type {HTMLImageElement[]} Slide images that are still loading. */
  #imageNodes = [];

  /** @type {number} Tallest measured slide height at the desktop breakpoint. */
  #slideHeight = 0;

  /** @type {number} Autoplay delay in milliseconds. */
  #autoplayDelay = Number(this.#emblaViewport?.dataset.autoplay);

  #handleBreakpointChange = () => {
    this.#initCarouselHeight();
    this.#emblaApi?.reInit();
  };

  /** @type {() => void} */
  #handleVideoBreakpointChange = () => {
    this.#syncHeroVideos();
  };

  #handleContentLoad = () => {
    this.#initCarouselHeight(true);
  };

  connectedCallback() {
    this.#desktopMedia.addEventListener('change', this.#handleVideoBreakpointChange);
    this.#syncHeroVideos();

    if (!this.#emblaViewport || !this.#emblaContainer) return;

    this.#initCarouselHeight();
    this.#initEmblaCarousel();
    this.#addMediaListeners();
    this.#addImageLoadListeners();
  }

  disconnectedCallback() {
    this.#desktopMedia.removeEventListener('change', this.#handleVideoBreakpointChange);
    this.#emblaApi?.destroy();
    this.#emblaApi = undefined;
    this.#desktopMedia.removeEventListener('change', this.#handleBreakpointChange);
    this.#imageNodes.forEach((image) => {
      image.removeEventListener('load', this.#handleContentLoad);
    });
  }

  /**
   * Instantiates the Embla carousel with axis-switching breakpoints, autoplay,
   * and accessibility plugins, then wires up dots and event listeners.
   *
   * @returns {void}
   */
  #initEmblaCarousel() {
    const plugins = [Accessibility()];
    if (this.#autoplayDelay > 0) {
      plugins.push(Autoplay({ delay: this.#autoplayDelay }));
    }

    this.#emblaApi = EmblaCarousel(
      this.#emblaViewport,
      {
        axis: 'x',
        slidesToScroll: 'auto',
        containScroll: 'trimSnaps',
        breakpoints: {
          '(min-width: 1024px)': {
            slidesToScroll: 2,
            axis: 'y',
          },
          '(max-width: 1023px)': {
            dragFree: true,
          },
        },
      },
      plugins
    );

    playAutoplay(this.#emblaApi);

    addDots(this.#emblaApi);
    addAccessibility(this.#emblaApi);

    this.#addListeners();
  }

  /**
   * Attaches Embla event listeners that keep the carousel height CSS custom
   * property and dot active state in sync with viewport changes and slide selection.
   *
   * @returns {void}
   */
  #addListeners() {
    this.#emblaApi
      .on('resize', () => {
        this.#initCarouselHeight(true);
      })
      .on('reinit', () => {
        playAutoplay(this.#emblaApi);
        this.#initCarouselHeight();
      });
  }

  /**
   * Registers listeners that resync equal heights when crossing the desktop
   * breakpoint.
   *
   * @returns {void}
   */
  #addMediaListeners() {
    this.#desktopMedia.addEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Registers one-time load listeners for slide images that can affect natural
   * slide height after the initial carousel measurement.
   *
   * @returns {void}
   */
  #addImageLoadListeners() {
    this.#imageNodes = [...this.querySelectorAll('.embla__slide img')].filter(
      (image) => !image.complete
    );
    this.#imageNodes.forEach((image) => {
      image.addEventListener('load', this.#handleContentLoad, { once: true });
    });
  }

  /**
   * Plays the hero video for the current breakpoint. Both files use
   * `preload="none"`, so only a call to `play()` starts a download.
   *
   * @returns {void}
   */
  #syncHeroVideos() {
    const desktop = this.#isDesktop();

    this.#heroVideos().forEach((video) => {
      const active = (video.dataset.heroVideo === 'desktop') === desktop;

      if (!active) {
        video.pause();
        return;
      }

      video.play().catch(() => {
        // Autoplay can reject when the document is hidden.
      });
    });
  }

  /**
   * Hero background videos live beside this element, on the section grid.
   *
   * @returns {HTMLVideoElement[]}
   */
  #heroVideos() {
    return [...(this.parentElement?.querySelectorAll('[data-hero-video]') ?? [])].filter(
      (video) => video instanceof HTMLVideoElement
    );
  }

  /**
   * Returns whether the carousel is currently at the desktop breakpoint.
   *
   * @returns {boolean}
   */
  #isDesktop() {
    return this.#desktopMedia.matches;
  }

  /**
   * Gets the direct Embla slide elements.
   *
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    return [...this.#emblaContainer.children].filter((slide) => slide instanceof HTMLElement);
  }

  /**
   * Returns the vertical gap between desktop slides in pixels.
   *
   * @returns {number}
   */
  #getContainerGap() {
    const containerStyle = window.getComputedStyle(this.#emblaContainer);
    return (
      parseFloat(containerStyle.getPropertyValue('row-gap')) ||
      parseFloat(containerStyle.getPropertyValue('gap')) ||
      0
    );
  }

  /**
   * Measures every slide at its natural height and returns the tallest value.
   *
   * @returns {number}
   */
  #getTallestSlideHeight() {
    this.#emblaViewport.style.removeProperty('--hero-banner-slide-height');

    return this.#getSlides().reduce(
      (height, slide) => Math.max(height, Math.round(slide.getBoundingClientRect().height)),
      0
    );
  }

  /**
   * Applies the desktop slide and viewport height CSS custom properties.
   * Slides after the second stay out of flow until this runs, so the first
   * paint already reserves two slides.
   *
   * @param {number} slideHeight Tallest measured slide height in pixels.
   * @returns {void}
   */
  #setDesktopCarouselHeight(slideHeight) {
    const carouselHeight = slideHeight * DESKTOP_SLIDES_PER_VIEW + this.#getContainerGap();

    this.#emblaViewport.style.setProperty('--hero-banner-slide-height', `${slideHeight}px`);
    this.#emblaViewport.style.setProperty('--selected-snap-height', `${carouselHeight}px`);
    this.#emblaContainer.removeAttribute('data-pending');
  }

  /**
   * Clears desktop-only equal-height custom properties and puts overflow
   * slides back out of flow until the next desktop measurement.
   *
   * @returns {void}
   */
  #clearCarouselHeight() {
    this.#slideHeight = 0;
    this.#emblaViewport.style.removeProperty('--hero-banner-slide-height');
    this.#emblaViewport.style.setProperty('--selected-snap-height', 'auto');
    this.#emblaContainer.setAttribute('data-pending', '');
  }

  /**
   * Keeps desktop slides equal to the tallest slide and sizes the viewport to
   * two slides plus the carousel's vertical gap.
   *
   * @param {boolean} [reinit=false] Reinitializes Embla when the measured slide height changes.
   * @returns {void}
   */
  #initCarouselHeight(reinit = false) {
    if (!this.#isDesktop()) {
      this.#clearCarouselHeight();
      return;
    }

    const slideHeight = this.#getTallestSlideHeight();
    const hasChanged = slideHeight !== this.#slideHeight;

    if (!slideHeight) return;

    this.#slideHeight = slideHeight;
    this.#setDesktopCarouselHeight(slideHeight);

    if (hasChanged && reinit) {
      this.#emblaApi?.reInit();
    }
  }
}

if (!customElements.get('hero-banner-cards')) {
  customElements.define('hero-banner-cards', HeroBannerCards);
}
